/**
 * Determines if dynamic parlevels should apply to a planogram item
 * based on the machine's dynamic parlevel settings and the product's fresh food status.
 *
 * @param {Object} planogramItem - The planogram item to check
 * @param {Object} machine - The machine with dynamic parlevel settings
 * @returns {boolean} True if dynamic parlevels should apply (parlevel is locked), false otherwise
 */
export const shouldApplyDynamicParlevels = (planogramItem, machine) => {
	// If machine doesn't use dynamic parlevels, they don't apply
	if (!machine?.usesDynamicParlevels) { return false; }

	// If there's no product, dynamic parlevels don't apply
	if (!planogramItem?.product) { return false; }

	const settings = machine.dynamicParlevelsSettings;
	// If no settings are configured, dynamic parlevels don't apply
	if (!settings) { return false; }

	// Check if the product is fresh food
	const { isFreshFood } = planogramItem.product;

	// Default both flags to true if not explicitly set to false
	const applyToFreshFood = settings.applyToFreshFood !== false;
	const applyToNonFreshFood = settings.applyToNonFreshFood !== false;
	const machineAppliesDynamicParlevels = isFreshFood ? applyToFreshFood : applyToNonFreshFood;

	// Product overrides should still participate even when the machine-level
	// category scope left the item unlocked.
	if (
		planogramItem.product.overrideDynamicParlevelsSettings &&
		!machineAppliesDynamicParlevels &&
		!planogramItem.ignoreProductDynamicParlevelsOverride
	) {
		return true;
	}

	// If parlevel is manually unlocked, dynamic parlevels don't apply
	if (planogramItem?.parlevelUnlocked) { return false; }

	return machineAppliesDynamicParlevels;
};

/**
 * Helper function to check if parlevel is editable
 * (inverse of shouldApplyDynamicParlevels)
 *
 * @param {Object} planogramItem - The planogram item to check
 * @param {Object} machine - The machine with dynamic parlevel settings
 * @returns {boolean} True if parlevel is editable, false if locked by dynamic parlevels
 */
export const isParlevelEditable = (planogramItem, machine) => {
	return !shouldApplyDynamicParlevels(planogramItem, machine);
};

/**
 * Returns the scope describing which products dynamic parlevels apply to.
 *
 * @param {Object} settings - Dynamic parlevel settings from the machine
 * @returns {'all' | 'fresh-only' | 'non-fresh-only' | 'none'} Scope identifier
 */
export const resolveDynamicParlevelsProductScope = settings => {
	if (!settings) { return 'all'; }

	const applyToFreshFood = settings.applyToFreshFood !== false;
	const applyToNonFreshFood = settings.applyToNonFreshFood !== false;

	if (applyToFreshFood && applyToNonFreshFood) { return 'all'; }
	if (applyToFreshFood) { return 'fresh-only'; }
	if (applyToNonFreshFood) { return 'non-fresh-only'; }

	return 'none';
};

/**
 * Converts the scope into a human-readable label for UI badges.
 *
 * @param {boolean} usesDynamicParlevels - Whether the machine uses dynamic parlevels
 * @param {Object} settings - Dynamic parlevel settings from the machine
 * @returns {string} Human-readable label for display
 */
export const getDynamicParlevelsStatusLabel = (usesDynamicParlevels, settings) => {
	if (!usesDynamicParlevels) { return 'off'; }

	const scope = resolveDynamicParlevelsProductScope(settings);

	switch (scope) {
		case 'fresh-only':
			return 'fresh only';
		case 'non-fresh-only':
			return 'non-fresh only';
		case 'none':
			return 'off';
		default:
			return 'all products';
	}
};
